import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import StepCard from "../StepCard";
import ImageWithFallback from "../ImageWithFallback";
import { useIsMobile } from "@/hooks/use-mobile";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { Badge } from "@/components/ui/badge";
import { PREFERENCE_KEYS, SlicerType } from "@/constants/calibrationKeys";

interface SlicerSelectionProps {
  selectedSlicer: SlicerType;
  onSlicerChange: (slicer: SlicerType) => void;
  onNext: () => void;
  onPrevious: () => void;
}

const SlicerSelection = ({ selectedSlicer, onSlicerChange, onNext, onPrevious }: SlicerSelectionProps) => {
  // Get current theme from document
  const getCurrentTheme = () => {
    if (typeof document !== 'undefined') {
      return document.documentElement.getAttribute('data-theme') || 'light';
    }
    return 'light';
  };

  const theme = getCurrentTheme();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [rememberSelection, setRememberSelection] = useState(false);
  const [savedSlicer, setSavedSlicer] = useState<string | null>(null);

  useEffect(() => {
    const saved = localStorage.getItem(PREFERENCE_KEYS.slicer);
    if (saved === "creality" || saved === "orca" || saved === "anycubic") {
      setSavedSlicer(saved);
      setRememberSelection(true);
      onSlicerChange(saved);
      // Automatically proceed to next step if there's a saved preference
      setTimeout(() => {
        onNext();
      }, 100);
    }
  }, [onSlicerChange, onNext]);

  const handleSlicerSelect = (slicer: SlicerType) => {
    onSlicerChange(slicer);
    
    if (rememberSelection) {
      localStorage.setItem(PREFERENCE_KEYS.slicer, slicer);
      const slicerNames = {
        creality: "Creality Print",
        orca: "Orca Slicer",
        anycubic: "Anycubic Next"
      };
      toast({
        title: "Preference Saved",
        description: `${slicerNames[slicer]} will be remembered for next time.`,
      });
    }
    
    onNext();
  };

  const handlePrevious = () => {
    onPrevious();
  };

  const handleRememberChange = (checked: boolean) => {
    setRememberSelection(checked);
    if (!checked) {
      localStorage.removeItem(PREFERENCE_KEYS.slicer);
      setSavedSlicer(null);
      toast({
        title: "Preference Removed",
        description: "Slicer preference has been cleared.",
      });
    }
  };

  const clearSavedPreference = () => {
    localStorage.removeItem(PREFERENCE_KEYS.slicer);
    setSavedSlicer(null);
    setRememberSelection(false);
    toast({
      title: "Preference Cleared",
      description: "Saved slicer preference has been removed.",
    });
  };

  const getCrealityLogo = () => {
    return "/lovable-uploads/d8167ef4-72cd-4789-8b88-96da916e34b5.png";
  };

  const getOrcaLogo = () => {
    return "/lovable-uploads/81524e49-529c-48a4-8476-0335cdaf883a.png";
  };

  const getAnycubicLogo = () => {
    return "/lovable-uploads/4fe1e469-0db2-4fb3-a7e4-14c78db1c06f.png";
  };

  const getSlicerDisplayName = (slicer: string) => {
    const names = {
      creality: "Creality Print",
      orca: "Orca Slicer",
      anycubic: "Anycubic Next"
    };
    return names[slicer as keyof typeof names] || slicer;
  };

  const borderClass = theme === "dark" ? "border-gray-500" : "border-input";

  return (
    <StepCard
      title=""
      onPrevious={onPrevious}
      showPrevious
      customRightButton={
        <div className="flex items-center space-x-2">
          <Checkbox
            id="remember-slicer"
            checked={rememberSelection}
            onCheckedChange={handleRememberChange}
          />
          <label htmlFor="remember-slicer" className="text-sm">
            {isMobile ? "Remember" : "Remember slicer"}
          </label>
        </div>
      }
    >
      <div className="space-y-4">
        <div className="text-center mb-6">
          <h3 className="text-lg font-semibold text-foreground mb-2">Choose Your Slicer</h3>
          <p className="text-muted-foreground text-sm">Select which slicer you're using</p>
        </div>

        {savedSlicer && (
          <div className="bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="font-semibold text-blue-800 dark:text-blue-200 mb-1">
                  Saved Preference
                </h4>
                <p className="text-blue-700 dark:text-blue-300 text-sm">
                  You previously saved <strong>{getSlicerDisplayName(savedSlicer)}</strong> as your preferred slicer.
                </p>
              </div>
              <Button
                onClick={clearSavedPreference}
                variant="outline"
                size="sm"
                className="border-blue-300 dark:border-blue-600 text-blue-700 dark:text-blue-300"
              >
                Clear
              </Button>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 gap-4">
          <Button
            onClick={() => handleSlicerSelect("creality")}
            variant="outline"
            className={`h-auto p-0 text-left justify-start overflow-hidden transition-all duration-200 hover:scale-[1.02] hover:shadow-md hover:bg-muted/50 ${borderClass} ${selectedSlicer === "creality" ? "ring-2 ring-blue-500" : ""}`}
          >
            <div className="flex w-full h-24 sm:h-32">
              <div className="w-1/3 flex items-center justify-center p-3 sm:p-4 bg-background">
                <ImageWithFallback
                  src={getCrealityLogo()}
                  alt="Creality Print" 
                  className="max-h-full max-w-full object-contain"
                  fallbackSrc="/lovable-uploads/9a562680-9186-4e34-bf50-4a212495e123.png"
                />
              </div>
              <div className="flex-1 p-3 sm:p-4 flex items-center justify-between bg-background">
                <div className="font-bold text-lg sm:text-xl">
                  Creality Print
                </div>
                {savedSlicer === "creality" && (
                  <Badge variant="secondary">Saved</Badge>
                )}
              </div>
            </div>
          </Button>
          
          <Button
            onClick={() => handleSlicerSelect("orca")}
            variant="outline"
            className={`h-auto p-0 text-left justify-start overflow-hidden transition-all duration-200 hover:scale-[1.02] hover:shadow-md hover:bg-muted/50 ${borderClass} ${selectedSlicer === "orca" ? "ring-2 ring-blue-500" : ""}`}
          >
            <div className="flex w-full h-24 sm:h-32">
              <div className="w-1/3 flex items-center justify-center p-3 sm:p-4 bg-background">
                <ImageWithFallback
                  src={getOrcaLogo()}
                  alt="Orca Slicer" 
                  className="max-h-full max-w-full object-contain"
                />
              </div>
              <div className="flex-1 p-3 sm:p-4 flex items-center justify-between bg-background">
                <div className="font-bold text-lg sm:text-xl">
                  Orca Slicer
                </div>
                {savedSlicer === "orca" && (
                  <Badge variant="secondary">Saved</Badge>
                )}
              </div>
            </div>
          </Button>

          <Button
            onClick={() => handleSlicerSelect("anycubic")}
            variant="outline"
            className={`h-auto p-0 text-left justify-start overflow-hidden transition-all duration-200 hover:scale-[1.02] hover:shadow-md hover:bg-muted/50 ${borderClass} ${selectedSlicer === "anycubic" ? "ring-2 ring-blue-500" : ""}`}
          >
            <div className="flex w-full h-24 sm:h-32">
              <div className="w-1/3 flex items-center justify-center p-3 sm:p-4 bg-background">
                <ImageWithFallback
                  src={getAnycubicLogo()}
                  alt="Anycubic Next" 
                  className="max-h-full max-w-full object-contain"
                />
              </div>
              <div className="flex-1 p-3 sm:p-4 flex items-center justify-between bg-background">
                <div className="font-bold text-lg sm:text-xl">
                  Anycubic Next
                </div>
                {savedSlicer === "anycubic" && (
                  <Badge variant="secondary">Saved</Badge>
                )}
              </div>
            </div>
          </Button>
        </div>
      </div>
    </StepCard>
  );
};

export default SlicerSelection;
